import React, { useEffect, useMemo, useState } from "react";
import { Calendar, ArrowLeft, ChevronLeft, ChevronRight, Search, CheckCircle2, Clock, ExternalLink, Trash2, MessageSquare, Send, Layers, Sparkles, ImageIcon, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import RoleBadge from "@/components/RoleBadge";

/**
 * Calendar-first Social Posts Timeline (admin / RPM / anyone with access).
 * Step 1 — grid of events/projects with post counts. Filters: event, project, author-role, author, search.
 * Step 2 — click a card → month calendar with post-dot chips per day.
 * Step 3 — click a day → posts on that day with designer attachment + remark thread.
 */
export default function SocialPostsTimelinePage() {
  const { user, customRole } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [filters, setFilters] = useState({ event_id: "", project_id: "", role: "", created_by: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [container, setContainer] = useState(null); // {kind, id, name} — event/project drill-in
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddPost, setShowAddPost] = useState(null); // { date, container }
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && k !== "q") params[k] = v; });
      const [tl, u, e, p, cr] = await Promise.all([
        api.get("/social-posts/timeline", { params }),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/events").catch(() => ({ data: [] })),
        api.get("/sm-projects").catch(() => ({ data: [] })),
        api.get("/admin/custom-roles").catch(() => ({ data: [] })),
      ]);
      setRows(tl.data || []);
      setUsers(u.data || []); setEvents(e.data || []); setProjects(p.data || []); setCustomRoles(cr.data || []);
    } catch { toast.error("Failed to load timeline"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.event_id, filters.project_id, filters.role, filters.created_by]);

  const isAdmin = user.role === "admin";
  const canCreatePosts = ["admin", "regional_pm", "social_media_manager", "social_media_executive", "project_manager"].includes(user.role);

  // Text-only filter runs client-side
  const filtered = useMemo(() => {
    if (!filters.q) return rows;
    const s = filters.q.toLowerCase();
    return rows.filter((x) =>
      (x.title || "").toLowerCase().includes(s) ||
      (x.author_name || "").toLowerCase().includes(s) ||
      (x.container_name || "").toLowerCase().includes(s)
    );
  }, [rows, filters.q]);

  // Group posts by container (event / project) — include ALL known events/projects
  // even if they have zero posts yet, so users can still open the calendar
  // and schedule the first post for an event. Posts pointing to deleted
  // events/projects (orphans) are dropped so the Timeline mirrors the live
  // Events / Projects list exactly.
  const containers = useMemo(() => {
    const map = new Map();
    // Seed with every event + project the current user can see
    events.forEach((e) => {
      map.set(`event:${e.event_id}`, { kind: "event", id: e.event_id, name: e.name, posts: [], postedCount: 0 });
    });
    projects.forEach((p) => {
      map.set(`project:${p.project_id}`, { kind: "project", id: p.project_id, name: p.name, posts: [], postedCount: 0 });
    });
    // Overlay post counts — ignore posts whose container no longer exists (orphans)
    filtered.forEach((p) => {
      const key = `${p.container_kind}:${p.event_id}`;
      const c = map.get(key);
      if (!c) return; // orphan post — parent event/project has been deleted
      c.posts.push(p);
      if (p.posted) c.postedCount += 1;
    });
    let arr = Array.from(map.values());
    if (filters.event_id) arr = arr.filter((c) => c.kind === "event" && c.id === filters.event_id);
    if (filters.project_id) arr = arr.filter((c) => c.kind === "project" && c.id === filters.project_id);
    if (filters.q) {
      const s = filters.q.toLowerCase();
      arr = arr.filter((c) => (c.name || "").toLowerCase().includes(s));
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [filtered, events, projects, filters.event_id, filters.project_id, filters.q]);

  const allRoles = useMemo(() => {
    const built = ["social_media_manager", "social_media_executive", "head_media_comm", "media_comm_executive", "designer", "admin"];
    return Array.from(new Set([...built, ...customRoles.map((c) => c.role_key)]));
  }, [customRoles]);

  const postsInContainer = useMemo(() => {
    if (!container) return [];
    return filtered.filter((p) => p.container_kind === container.kind && p.event_id === container.id);
  }, [filtered, container]);

  const postsByDate = useMemo(() => {
    const m = {};
    postsInContainer.forEach((p) => {
      const d = (p.scheduled_date || "").slice(0, 10);
      if (!d) return;
      (m[d] = m[d] || []).push(p);
    });
    return m;
  }, [postsInContainer]);

  const filterBar = (
    <div className="dashboard-card p-4 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input className="input-pill text-sm w-full pl-9" placeholder="Search posts, authors…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="tl-search" />
        </div>
        <select className="input-pill text-sm" value={filters.event_id} onChange={(e) => setFilters({ ...filters, event_id: e.target.value, project_id: "" })} data-testid="tl-event">
          <option value="">All events</option>
          {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.name}</option>)}
        </select>
        <select className="input-pill text-sm" value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value, event_id: "" })} data-testid="tl-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>✦ {p.name}</option>)}
        </select>
        <select className="input-pill text-sm" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="tl-role">
          <option value="">All roles</option>
          {allRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <select className="input-pill text-sm" value={filters.created_by} onChange={(e) => setFilters({ ...filters, created_by: e.target.value })} data-testid="tl-author">
          <option value="">All authors</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
      </div>
    </div>
  );

  if (container) {
    // ============= FULL CALENDAR VIEW =============
    const first = monthStart;
    const startDow = first.getDay();
    const cells = [];
    const day0 = new Date(first); day0.setDate(1 - startDow);
    for (let i = 0; i < 42; i++) {
      const d = new Date(day0); d.setDate(day0.getDate() + i);
      // LOCAL date string — avoid toISOString() timezone shift that puts
      // IST users a day behind.
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      cells.push({ date: d, iso, inMonth: d.getMonth() === first.getMonth() });
    }
    const selectedPosts = selectedDate ? (postsByDate[selectedDate] || []) : [];

    return (
      <Layout navItems={getNav(user.role, customRole)}>
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
          <button onClick={() => { setContainer(null); setSelectedDate(null); }} className="text-sm text-slate-500 hover:text-orange-600 flex items-center gap-1 mb-3" data-testid="back-to-events">
            <ArrowLeft className="w-4 h-4" /> Back to all events
          </button>
          <div className="dashboard-card p-5 mb-5 flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white font-display font-black ${container.kind === "project" ? "bg-gradient-to-br from-fuchsia-500 to-pink-500" : "brand-gradient"}`}>
              {container.kind === "project" ? <Sparkles className="w-6 h-6" /> : container.name?.charAt(0) || "E"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{container.kind === "project" ? "Project" : "Event"} Calendar</div>
              <h1 className="font-display font-black text-2xl truncate">{container.name}</h1>
              <div className="text-xs text-slate-500 mt-0.5">{postsInContainer.length} post{postsInContainer.length === 1 ? "" : "s"} · {postsInContainer.filter((p) => p.posted).length} posted · {postsInContainer.filter((p) => !p.posted).length} pending</div>
            </div>
          </div>

          {filterBar}

          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 dashboard-card p-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1))} className="btn-ghost text-xs px-2" data-testid="cal-prev"><ChevronLeft className="w-3 h-3" /></button>
                <div className="font-display font-black text-lg" data-testid="cal-month-label">{monthStart.toLocaleString("default", { month: "long", year: "numeric" })}</div>
                <button onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))} className="btn-ghost text-xs px-2" data-testid="cal-next"><ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 uppercase mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((c) => {
                  const dayPosts = postsByDate[c.iso] || [];
                  const isSelected = selectedDate === c.iso;
                  const anyPosted = dayPosts.some((p) => p.posted);
                  return (
                    <button key={c.iso} onClick={() => setSelectedDate(c.iso)}
                      className={`aspect-square p-1.5 rounded-lg text-xs transition-all border ${
                        isSelected ? "border-orange-500 bg-orange-50" :
                        c.inMonth ? "border-slate-100 hover:border-orange-200" : "border-transparent opacity-40"
                      }`}
                      data-testid={`cal-day-${c.iso}`}>
                      <div className="text-right font-bold text-slate-700">{c.date.getDate()}</div>
                      {dayPosts.length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-0.5 justify-center">
                          {dayPosts.slice(0, 4).map((p) => (
                            <span key={p.post_id} className={`w-1.5 h-1.5 rounded-full ${p.posted ? "bg-emerald-500" : "bg-amber-500"}`}></span>
                          ))}
                          {dayPosts.length > 4 && <span className="text-[8px] text-slate-500">+{dayPosts.length - 4}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-4 text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Posted</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Scheduled</span>
              </div>
            </div>

            <div className="dashboard-card p-5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="font-bold flex items-center gap-2"><Calendar className="w-4 h-4 text-orange-600" /> {selectedDate || "Pick a date"}</h4>
                {selectedDate && canCreatePosts && (
                  <button
                    onClick={() => setShowAddPost({ date: selectedDate, container })}
                    className="btn-primary text-xs py-1.5 px-2"
                    data-testid="tl-add-post-btn"
                  ><Plus className="w-3 h-3" /> Add post</button>
                )}
              </div>
              {!selectedDate ? (
                <p className="text-sm text-slate-400 mt-4">Click any calendar day to inspect its posts, designer attachments and remarks.</p>
              ) : selectedPosts.length === 0 ? (
                <p className="text-sm text-slate-400 mt-4">Nothing scheduled on this date.{canCreatePosts && " Use \u201cAdd post\u201d to schedule one."}</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {selectedPosts.map((p) => (
                    <PostCard key={p.post_id} post={p} customRoles={customRoles} onReload={load} canDelete={isAdmin || p.can_edit} canReschedule={canCreatePosts && p.can_edit} currentUser={user} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {showAddPost && (
          <AddPostModal
            container={showAddPost.container}
            date={showAddPost.date}
            onClose={() => setShowAddPost(null)}
            onSaved={() => { setShowAddPost(null); load(); }}
          />
        )}
      </Layout>
    );
  }

  // ============= EVENT/PROJECT CARD GRID =============
  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="social-timeline-page">
        <PageHeader title="Social Posts Timeline" subtitle="Every event you can access, whether it has posts scheduled yet or not. Click a card to open its calendar." icon={Calendar} accent="pink" />
        {filterBar}
        {loading ? (
          <div className="dashboard-card p-12 text-center text-slate-400">Loading…</div>
        ) : containers.length === 0 ? (
          <div className="dashboard-card p-12 text-center text-slate-400"><Layers className="w-8 h-8 mx-auto mb-2" /> No events or projects match these filters.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {containers.map((c) => (
              <button key={`${c.kind}:${c.id}`} onClick={() => { setContainer(c); setSelectedDate(null); }} className="dashboard-card p-5 text-left hover:border-orange-300 transition-all" data-testid={`tl-container-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-display font-black ${c.kind === "project" ? "bg-gradient-to-br from-fuchsia-500 to-pink-500" : "brand-gradient"}`}>
                      {c.kind === "project" ? <Sparkles className="w-5 h-5" /> : c.name?.charAt(0) || "E"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.kind === "project" ? "Project" : "Event"}</div>
                      <div className="font-display font-bold text-slate-900 truncate">{c.name}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">{c.posts.length} total</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">{c.postedCount} posted</span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold">{c.posts.length - c.postedCount} pending</span>
                </div>
                <div className="mt-3 text-xs text-slate-500">Open calendar <span className="text-orange-600 font-bold">→</span></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function PostCard({ post, customRoles, onReload, canDelete, canReschedule, currentUser }) {
  const [remarks, setRemarks] = useState(post.remarks || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [movePickerDate, setMovePickerDate] = useState(post.scheduled_date || "");

  const toggleStatus = async () => {
    const nextPosted = !post.posted;
    if (nextPosted && !window.confirm(`Mark "${post.title || "Untitled"}" as POSTED? This stamps who posted it and when.`)) return;
    if (!nextPosted && !window.confirm("Un-mark this post as posted?")) return;
    try {
      await api.patch(`/social-posts/${post.post_id}`, { posted: nextPosted });
      toast.success(nextPosted ? "Marked as posted" : "Reverted to pending");
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/social-posts/${post.post_id}/remark`, { text: text.trim() });
      setRemarks((rs) => [...rs, r.data]);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${post.title || "Untitled"}"? This removes it from everyone's calendar.`)) return;
    try {
      await api.delete(`/social-posts/${post.post_id}`);
      toast.success("Deleted");
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const move = async () => {
    if (!movePickerDate || movePickerDate === post.scheduled_date) return;
    try {
      await api.patch(`/social-posts/${post.post_id}`, { scheduled_date: movePickerDate });
      toast.success(`Moved to ${movePickerDate}`);
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delRemark = async (rid) => {
    try {
      await api.delete(`/social-posts/${post.post_id}/remarks/${rid}`);
      setRemarks((rs) => rs.filter((r) => r.remark_id !== rid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-3 rounded-xl border border-slate-100" data-testid={`tl-post-${post.post_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${post.posted ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {post.posted ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
            </span>
            <span className="text-xs font-bold text-slate-800 truncate">{post.title || "Untitled"}</span>
            <span className="text-[9px] uppercase font-bold text-slate-400">{post.platform || "—"}</span>
          </div>
          <div className="text-[10px] text-slate-500 flex items-center gap-1 flex-wrap mt-0.5">
            <span>By <b>{post.author_name || "—"}</b></span>
            {post.author_role && <RoleBadge role={post.author_role} customRoles={customRoles} size="xs" />}
          </div>
        </div>
        {canDelete && <button onClick={del} className="text-rose-500 p-1 rounded hover:bg-rose-50" title="Delete post"><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>

      {post.can_mark_posted && (
        <button
          onClick={toggleStatus}
          className={`mt-2 w-full text-xs font-bold py-1.5 px-2 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
            post.posted
              ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
              : "bg-emerald-500 text-white hover:bg-emerald-600"
          }`}
          data-testid={`tl-toggle-posted-${post.post_id}`}
        >
          {post.posted ? (<><Clock className="w-3.5 h-3.5" /> Un-mark as posted</>) : (<><CheckCircle2 className="w-3.5 h-3.5" /> Mark as posted</>)}
        </button>
      )}
      {post.posted && post.posted_by_name && (
        <div className="mt-1 text-[10px] text-emerald-700 text-center">Posted by <b>{post.posted_by_name}</b>{post.posted_at ? ` · ${new Date(post.posted_at).toLocaleString()}` : ""}</div>
      )}

      {canReschedule && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap bg-slate-50 rounded-lg p-2" data-testid={`tl-move-${post.post_id}`}>
          <span className="text-[9px] uppercase font-bold text-slate-500">Reschedule to</span>
          <input
            type="date"
            value={movePickerDate}
            onChange={(e) => setMovePickerDate(e.target.value)}
            className="text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-white"
            data-testid={`tl-move-input-${post.post_id}`}
          />
          <button
            onClick={move}
            disabled={!movePickerDate || movePickerDate === post.scheduled_date}
            className="text-[11px] px-2 py-0.5 rounded bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`tl-move-btn-${post.post_id}`}
          >Move</button>
        </div>
      )}

      {post.description && (
        <div className="mt-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">Description / caption</div>
          <div className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-2" data-testid={`tl-desc-${post.post_id}`}>{post.description}</div>
        </div>
      )}
      {post.content_idea && (
        <div className="mt-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">Content idea / script</div>
          <div className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-2">{post.content_idea}</div>
        </div>
      )}
      {Array.isArray(post.custom_fields) && post.custom_fields.filter((cf) => cf.value).length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">Custom fields</div>
          <div className="space-y-1">
            {post.custom_fields.filter((cf) => cf.value).map((cf, i) => (
              <div key={i} className="text-xs bg-slate-50 rounded-lg p-2">
                <div className="text-[9px] uppercase font-bold text-slate-500">{cf.label || cf.key || `Field ${i + 1}`}</div>
                <div className="text-slate-700 whitespace-pre-wrap">{cf.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(post.assets) && post.assets.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">Attached assets</div>
          <div className="flex flex-wrap gap-1.5">
            {post.assets.map((a, i) => {
              const url = a.url || a; const label = a.label || url;
              return <a key={i} href={url} target="_blank" rel="noreferrer" className="text-[10px] text-sky-700 hover:underline break-all inline-flex items-center gap-1"><ExternalLink className="w-2.5 h-2.5" /> {label}</a>;
            })}
          </div>
        </div>
      )}
      {post.image_url && (
        <div className="mt-2">
          <img src={post.image_url} alt="" className="w-full max-h-48 object-cover rounded border border-slate-100" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      )}

      {post.design_link ? (
        <div className="mt-3 pt-2 border-t border-fuchsia-100 bg-fuchsia-50/40 -mx-3 px-3 pb-2" data-testid={`tl-design-${post.post_id}`}>
          <div className="text-[9px] uppercase font-bold text-fuchsia-700 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Designer submission</div>
          {post.design_thumbnail ? (
            <a href={post.design_link} target="_blank" rel="noreferrer" className="block">
              <img src={post.design_thumbnail} alt="Design" className="w-full max-h-40 object-cover rounded border border-fuchsia-100" onError={(e) => { e.target.style.display = "none"; }} />
            </a>
          ) : null}
          <a href={post.design_link} target="_blank" rel="noreferrer" className="text-[11px] text-fuchsia-700 font-semibold hover:underline inline-flex items-center gap-1 mt-1 break-all"><ExternalLink className="w-3 h-3" /> {post.design_link}</a>
          <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
            {post.design_added_by_name && <span>by <b>{post.design_added_by_name}</b></span>}
            {post.design_added_at && <span>{new Date(post.design_added_at).toLocaleDateString()}</span>}
          </div>
          {post.design_note && <div className="text-[10px] text-slate-600 italic mt-1 whitespace-pre-wrap">&ldquo;{post.design_note}&rdquo;</div>}
        </div>
      ) : (
        <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400 italic">No designer submission yet.</div>
      )}

      <div className="mt-3 pt-2 border-t border-slate-100">
        <div className="text-[9px] uppercase font-bold text-slate-500 mb-1.5 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Remarks ({remarks.length})</div>
        <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
          {remarks.length === 0 && <div className="text-[10px] text-slate-400 italic">No remarks yet.</div>}
          {remarks.map((r) => (
            <div key={r.remark_id} className="p-2 rounded-lg bg-slate-50" data-testid={`tl-remark-${r.remark_id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-slate-700">{r.author_name} <span className="font-normal text-slate-400">· {ROLE_LABELS[r.author_role] || r.author_role}</span></div>
                {(r.author_id === currentUser.user_id || currentUser.role === "admin") && (
                  <button onClick={() => delRemark(r.remark_id)} className="text-rose-400 hover:text-rose-600" title="Delete remark"><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
              <div className="text-xs text-slate-800 mt-0.5 whitespace-pre-wrap">{r.text}</div>
              <div className="text-[9px] text-slate-400 mt-0.5">{new Date(r.at).toLocaleString()}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          <input placeholder="Add a remark…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            className="input-pill py-1.5 text-xs flex-1" data-testid={`tl-remark-input-${post.post_id}`} />
          <button onClick={send} disabled={busy || !text.trim()} className="btn-primary py-1.5 px-2 disabled:opacity-50" data-testid={`tl-remark-send-${post.post_id}`}><Send className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}

function AddPostModal({ container, date, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", content_idea: "", platform: "instagram" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!container?.id) { toast.error("No event/project selected"); return; }
    setSaving(true);
    try {
      // Both events and projects share the same endpoint shape — the backend
      // uses whatever event_id is supplied in the URL and does not enforce
      // "must be an event" at insert time.
      await api.post(`/events/${container.id}/social-posts`, { ...form, scheduled_date: date });
      toast.success("Post scheduled");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tl-add-post-modal">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-500">Schedule a post</div>
            <h3 className="font-display font-black text-xl">{container?.name}</h3>
            <div className="text-xs text-slate-500 mt-0.5">Date · {date}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="tl-add-post-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <input
            className="input-pill"
            placeholder="Post title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="tl-add-post-title"
          />
          <select
            className="input-pill"
            value={form.platform}
            onChange={(e) => setForm({ ...form, platform: e.target.value })}
            data-testid="tl-add-post-platform"
          >
            {["instagram", "twitter", "linkedin", "facebook"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <textarea
            rows={2}
            className="input-pill resize-none"
            placeholder="Description / caption"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="tl-add-post-description"
          />
          <textarea
            rows={3}
            className="input-pill resize-none"
            placeholder="Content idea / script"
            value={form.content_idea}
            onChange={(e) => setForm({ ...form, content_idea: e.target.value })}
            data-testid="tl-add-post-idea"
          />
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !form.title.trim()}
            className="btn-primary px-5 disabled:opacity-50"
            data-testid="tl-add-post-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Send className="w-4 h-4" /> Schedule</>}
          </button>
        </div>
      </div>
    </div>
  );
}

